/**
 * Stappen van de productconfigurator.
 *
 * Vroeger: configureren, uploaden/ontwerpen, en dan een aparte derde stap met
 * alleen de prijs en een knop "Toevoegen aan winkelwagen". Die derde stap liet
 * niets nieuws zien. Nu staat de winkelwagenknop direct in de ontwerpstap,
 * zodra het ontwerp klaar is. Producten zonder drukbestand gaan vanaf het
 * configureren meteen naar de winkelwagen.
 *
 * De stap 'checkout' blijft bestaan voor beheer (orderpagina, ontwerp aanpassen
 * vanuit de winkelwagen), die daar met een bestaand bestand direct op landt.
 */
export type ConfiguratorStep = 'configure' | 'upload' | 'checkout';

export type ConfiguratorPrimaryAction = 'go-to-design' | 'add-to-cart' | 'await-design';

export function resolveConfiguratorPrimaryAction({
  step,
  isPrintable,
  designsReady,
  designMadeInline = false,
}: {
  step: ConfiguratorStep;
  isPrintable: boolean;
  designsReady: boolean;
  /** Ontwerp ontstaat al in de configuratiestap (vinyl-ontwerptool). */
  designMadeInline?: boolean;
}): ConfiguratorPrimaryAction {
  if (step === 'configure') {
    if (!isPrintable) return 'add-to-cart';
    return designMadeInline && designsReady ? 'add-to-cart' : 'go-to-design';
  }
  if (step === 'upload') return designsReady ? 'add-to-cart' : 'await-design';
  return 'add-to-cart';
}

export type AddToCartBlock = 'upload-first' | 'low-resolution' | null;

/**
 * Wat het toevoegen nog tegenhoudt aan de bestandskant. De lage-resolutiekeuze
 * zat alleen op de knop van de oude tussenstap; hij hoort bij het toevoegen zelf.
 * Beheer (opslaan van een bestaande order) houdt het oude gedrag.
 */
export function addToCartFileBlock({
  isPrintable,
  designCount,
  requiredDesignCount,
  hasUnacceptedLowResolution,
  isAdminSave,
}: {
  isPrintable: boolean;
  designCount: number;
  requiredDesignCount: number;
  hasUnacceptedLowResolution: boolean;
  isAdminSave: boolean;
}): AddToCartBlock {
  if (!isPrintable) return null;
  if (designCount < requiredDesignCount) return 'upload-first';
  if (hasUnacceptedLowResolution && !isAdminSave) return 'low-resolution';
  return null;
}

const FLOW_COPY = {
  nl: {
    goToDesign: 'Ontwerp uploaden of maken',
    stepDesignAndOrder: 'Ontwerp & bestellen',
    awaitingDesign: 'Upload je bestand of maak je ontwerp hierboven. Daarna voeg je het direct toe aan je winkelwagen.',
  },
  en: {
    goToDesign: 'Upload or create your design',
    stepDesignAndOrder: 'Design & order',
    awaitingDesign: 'Upload your file or create your design above. Then add it straight to your cart.',
  },
  de: {
    goToDesign: 'Design hochladen oder gestalten',
    stepDesignAndOrder: 'Design & Bestellung',
    awaitingDesign: 'Laden Sie oben Ihre Datei hoch oder gestalten Sie Ihr Design. Danach legen Sie es direkt in den Warenkorb.',
  },
  fr: {
    goToDesign: 'Importer ou créer votre design',
    stepDesignAndOrder: 'Design et commande',
    awaitingDesign: 'Importez votre fichier ou créez votre design ci-dessus. Ajoutez-le ensuite directement au panier.',
  },
} as const;

export function configuratorFlowCopy(locale: string) {
  const lang = String(locale || 'nl').slice(0, 2) as keyof typeof FLOW_COPY;
  return FLOW_COPY[lang] || FLOW_COPY.nl;
}

type FileUploadSlotLike = { showIf?: unknown };

/**
 * Uploadvakken die bij de gekozen samenstelling horen. Een vak zonder `showIf`
 * hoort altijd bij het product; met `showIf` moet elke genoemde optie een van
 * de toegestane waarden hebben.
 */
export function visibleFileUploadSlots<T extends FileUploadSlotLike>(
  slots: T[] | null | undefined,
  configuration: Record<string, unknown>,
): T[] {
  if (!Array.isArray(slots)) return [];
  return slots.filter((slot) => {
    const showIf = slot?.showIf;
    if (!showIf || typeof showIf !== 'object') return true;
    return Object.entries(showIf as Record<string, unknown>).every(([optionId, allowedValues]) => {
      const selectedValue = String(configuration[optionId] ?? '');
      const accepted = Array.isArray(allowedValues) ? allowedValues : [allowedValues];
      return accepted.map(String).includes(selectedValue);
    });
  });
}

/**
 * Het product heeft uploadvakken, maar bij deze keuze hoort er geen een,
 * bijvoorbeeld de paalbanner als "Alleen houder". Dan is er niets te drukken
 * en hoort de klant direct naar de winkelwagen te kunnen. Zonder deze regel
 * viel het aantal ontwerpen terug op 1 en bleef de klant in de uploadstap
 * hangen zonder winkelwagenknop.
 */
export function uploadSlotsRuleOutPrintFile(
  slots: FileUploadSlotLike[] | null | undefined,
  configuration: Record<string, unknown>,
): boolean {
  if (!Array.isArray(slots) || slots.length === 0) return false;
  // Alleen beslissen op echte keuzes: zolang een optie uit de slotregels nog
  // leeg is (bijvoorbeeld voordat de standaardwaarden geladen zijn), blijft
  // het product een drukproduct.
  const referenced = new Set(slots.flatMap((slot) =>
    slot?.showIf && typeof slot.showIf === 'object' ? Object.keys(slot.showIf as Record<string, unknown>) : []));
  for (const optionId of referenced) {
    const value = configuration[optionId];
    if (value === undefined || value === null || value === '') return false;
  }
  return visibleFileUploadSlots(slots, configuration).length === 0;
}
